/** Text helpers for the 28-column screens. Pure. */

import { hasGlyph } from '../../render/font';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** Word-wrap to lines of at most `width` characters; over-long words are cut. */
export function wrap(text: string, width: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const raw of text.split(/\s+/).filter((w) => w.length > 0)) {
    let word = raw;
    while (word.length > width) {
      if (line) {
        lines.push(line);
        line = '';
      }
      lines.push(word.slice(0, width));
      word = word.slice(width);
    }
    if (!word) continue;
    if (!line) line = word;
    else if (line.length + 1 + word.length <= width) line += ` ${word}`;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** 9340 -> `9,340`. */
export function commas(n: number): string {
  const s = String(Math.max(0, Math.floor(n)));
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `8 OCT 2026`. */
export function longDate(year: number, month: number, day: number): string {
  return `${day} ${MONTHS[month - 1] ?? '???'} ${year}`;
}

/** 1 -> `1ST`, 2 -> `2ND`, 11 -> `11TH`. */
export function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'TH' : (['TH', 'ST', 'ND', 'RD'][n % 10] ?? 'TH');
  return `${n}${suffix}`;
}

/** Upper case, with anything the 8 x 8 font cannot draw as `?`. */
export function printable(s: string): string {
  return [...s].map((ch) => (hasGlyph(ch) ? ch.toUpperCase() : '?')).join('');
}

/** `s` cut to `width` characters. */
export function clip(s: string, width: number): string {
  return [...s].slice(0, Math.max(0, width)).join('');
}

/** A label and a value at the two ends of a line `width` characters long (the value wins a squeeze). */
export function spread(label: string, value: string, width: number): string {
  const room = Math.max(0, width - [...value].length);
  return clip(label, Math.max(0, room - 1)).padEnd(room) + value;
}

/**
 * A two-column table as lines: each label padded to `labelW`, its value wrapped into what is left
 * of `width`, continuation lines indented under the value.
 */
export function tableLines(rows: readonly (readonly [string, string])[], labelW: number, width: number): string[] {
  const out: string[] = [];
  for (const [label, value] of rows) {
    const parts = wrap(value, Math.max(1, width - labelW));
    if (parts.length === 0) parts.push('');
    parts.forEach((part, i) => out.push((i === 0 ? clip(label, labelW - 1) : '').padEnd(labelW) + part));
  }
  return out;
}
